import { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import { getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { fmtTs } from '../lib/globalTz';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type ReportType = 'executive_summary' | 'incident_trends' | 'attack_techniques' | 'affected_assets' | 'sla_metrics' | 'vulnerability_posture' | 'ti_activity' | 'heartbeat' | 'custom';

interface ReportSection {
  id: string;
  title: string;
  summary: string;
  columns: string[];
  rows: Record<string, unknown>[];
  warnings: string[];
  skipped?: boolean;
}

interface ReportPayload {
  ok: true;
  id: string;
  customerId: string;
  customerName: string;
  reportType: ReportType;
  title: string;
  prompt: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  executiveSummary: string;
  recommendations: Array<{ priority: string; title: string; details: string }>;
  sections: ReportSection[];
  appendix: ReportSection[];
  diagnostics: string[];
  promptPlan?: {
    inferredLookback: Lookback;
    inferredReportType: ReportType;
    matchedIntents: string[];
    executedSections: string[];
    skippedSections: string[];
  };
  ai: { used: boolean; fallback: boolean; model: string | null; warning: string | null };
}

interface Props { customerId?: string; customerName?: string; }

const REPORT_TYPES: Array<{ id: ReportType; label: string; hint: string }> = [
  { id: 'executive_summary', label: 'Executive Summary', hint: 'Incidents, SLA, vulnerabilities, TI, recommendations and evidence.' },
  { id: 'incident_trends', label: 'Incident Trends', hint: 'Daily incident volume and major incident appendix.' },
  { id: 'attack_techniques', label: 'Top Attack Techniques', hint: 'MITRE/tactic/technique view from alerts.' },
  { id: 'affected_assets', label: 'Top Affected Assets', hint: 'Repeatedly affected devices, identities and entities.' },
  { id: 'sla_metrics', label: 'SLA Metrics', hint: 'Estimated SLA health from incident age and severity.' },
  { id: 'vulnerability_posture', label: 'Vulnerability Posture', hint: 'Defender TVM posture and top CVEs.' },
  { id: 'ti_activity', label: 'TI Activity', hint: 'Indicator volume, active/expired status and types.' },
  { id: 'heartbeat', label: 'Heartbeat Report', hint: 'Agent heartbeat health for the selected lookback.' },
  { id: 'custom', label: 'Custom Prompt', hint: 'Use the prompt box for report intent.' },
];

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportPdf(payload: ReportPayload, filenameBase: string) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  let y = 16;
  const pageHeight = pdf.internal.pageSize.getHeight();
  const addLine = (line: string, size = 9, bold = false) => {
    if (y > pageHeight - 18) { pdf.addPage(); y = 16; }
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(line, 182);
    for (const l of lines) {
      if (y > pageHeight - 18) { pdf.addPage(); y = 16; }
      pdf.text(String(l), margin, y);
      y += size >= 14 ? 7 : 5;
    }
  };
  addLine(payload.title || 'KloudSecure AI Report', 16, true);
  addLine(`${payload.customerName} · ${payload.lookback} · Generated ${fmtTs(payload.generatedAt)}`, 9);
  y += 3;
  addLine('Executive Summary', 12, true);
  addLine(payload.executiveSummary, 9);
  y += 2;
  addLine('Recommendations', 12, true);
  for (const rec of payload.recommendations.slice(0, 10)) addLine(`${rec.priority}: ${rec.title} - ${rec.details}`, 8.5);
  for (const section of [...payload.sections, ...payload.appendix].slice(0, 12)) {
    y += 3;
    addLine(section.title, 12, true);
    addLine(section.summary, 8.5);
    for (const row of section.rows.slice(0, 12)) {
      const line = section.columns.slice(0, 5).map((col) => `${col}: ${text(row[col], 80)}`).join(' | ');
      addLine(line, 7.8);
    }
  }
  pdf.save(`${filenameBase}.pdf`);
}

export function AiReportBuilderTab({ customerId, customerName }: Props) {
  const { canCapability } = useAuth();
  const [reportType, setReportType] = useState<ReportType>('executive_summary');
  const [lookback, setLookback] = useState<Lookback>('30d');
  const [prompt, setPrompt] = useState('');
  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('report_builder', 'read');
  const canGenerate = canCapability('report_builder', 'generate');
  const canRefresh = canCapability('report_builder', 'refresh_cache');
  const canPrompt = canCapability('report_builder', 'use_prompt');
  const canExport = canCapability('report_builder', 'export');

  async function generate(refresh = false) {
    if (!customerId) return;
    if (!canGenerate) {
      toast({ title: 'Permission denied', description: 'Report Builder → Generate Reports is required.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const effectivePrompt = canPrompt ? prompt : '';
      const resp = await fetch(`${getApiBase()}/report-builder/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, reportType, lookback, prompt: effectivePrompt, refresh }),
      });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Report generation failed (${resp.status})`);
      const data = parsed as ReportPayload;
      setPayload(data);
      const firstSection = [...data.sections, ...data.appendix].find((s) => s.rows.length > 0) ?? data.sections[0] ?? data.appendix[0];
      setSelectedSectionId(firstSection?.id ?? '');
      toast({ title: data.cacheHit ? 'Report loaded from DB cache' : 'Report generated', description: data.title });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function exportData(format: 'pdf' | 'csv' | 'html') {
    if (!customerId || !payload) return;
    setExportBusy(format);
    setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/report-builder/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, payload, format }),
      });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      const filenameBase = String(parsed.filenameBase || `kloudsecure-report-${customerId}`);
      if (format === 'csv') downloadText(`${filenameBase}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      if (format === 'html') downloadText(`${filenameBase}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      if (format === 'pdf') exportPdf(payload, filenameBase);
      toast({ title: `Report ${format.toUpperCase()} exported`, description: payload.title });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportBusy(null);
    }
  }

  const allSections = useMemo(() => payload ? [...payload.sections, ...payload.appendix] : [], [payload]);
  const selectedSection = useMemo(() => allSections.find((section) => section.id === selectedSectionId) ?? allSections[0] ?? null, [allSections, selectedSectionId]);
  const columns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => {
    const cols = selectedSection?.columns?.length ? selectedSection.columns : ['message'];
    return cols.map((column) => ({ key: column, header: column, headerText: column, value: (row) => row[column], render: (row) => text(row[column], 260) }));
  }, [selectedSection]);

  if (!canRead) {
    return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">AI Report Builder is hidden by Permissions Master. Enable AI Report Builder → Read for this role.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">Customer Reporting</div>
            <h2 className="text-2xl font-black text-foreground">AI-Powered Report Builder</h2>
            <p className="text-sm text-muted-foreground max-w-4xl">Generate customer reports from templates or natural-language prompts. Results are saved to DB cache so repeat views do not fetch every data source again unless Refresh is used.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Lookback</label>
              <select value={lookback} onChange={(event) => setLookback(event.target.value as Lookback)} className="mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm">
                <option value="24h">24 hours</option><option value="7d">7 days</option><option value="30d">30 days</option><option value="60d">60 days</option><option value="90d">90 days</option>
              </select>
            </div>
            <button type="button" onClick={() => void generate(false)} disabled={loading || !customerId || !canGenerate} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60">{loading ? 'Generating…' : 'Generate'}</button>
            {canRefresh && <button type="button" onClick={() => void generate(true)} disabled={loading || !customerId} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Refresh Cache</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('pdf')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{exportBusy === 'pdf' ? 'Exporting…' : 'Export PDF'}</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('csv')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Export CSV</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('html')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Export HTML</button>}
          </div>
        </div>
        {error && <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}
      </div>

      {!customerId && <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">Select a customer to generate reports.</div>}

      <div className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h3 className="text-lg font-black text-foreground">Report Selection</h3>
          <div className="mt-4 grid gap-2">
            {REPORT_TYPES.map((item) => (
              <button key={item.id} type="button" onClick={() => setReportType(item.id)} className={`rounded-xl border p-3 text-left transition-all ${reportType === item.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}>
                <div className="font-bold text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.hint}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3"><h3 className="text-lg font-black text-foreground">Prompt Box</h3>{!canPrompt && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">Prompt disabled by permissions</span>}</div>
          <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} disabled={!canPrompt} className="mt-4 min-h-[150px] w-full rounded-2xl border border-border bg-background p-4 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" placeholder="Example: Hey, please generate a Heartbeat report for the past 24 hours with stale agents and recommended remediation steps." />
          <div className="mt-3 text-xs text-muted-foreground">Prompt is optional. Template selection still works without AI prompt mode.</div>
        </div>
      </div>

      {payload && (
        <>
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h3 className="text-2xl font-black text-foreground">{payload.title}</h3><div className="mt-1 text-xs text-muted-foreground">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'}{payload.expiresAt ? ` · Cache expires ${fmtTs(payload.expiresAt)}` : ''}</div></div>
              <span className="rounded-full border border-border px-3 py-1 text-xs font-bold">{payload.ai.used ? `AI: ${payload.ai.model}` : 'Deterministic narrative'}</span>
            </div>
            <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground"><strong>Executive Summary:</strong><br />{payload.executiveSummary}</div>
            {payload.promptPlan && (
              <div className="mt-4 rounded-xl border border-border bg-background p-4 text-sm">
                <div className="font-bold text-foreground">Prompt Execution Plan</div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <div className="text-muted-foreground"><span className="font-semibold text-foreground">Inferred type:</span> {payload.promptPlan.inferredReportType.replace(/_/g, ' ')}</div>
                  <div className="text-muted-foreground"><span className="font-semibold text-foreground">Inferred lookback:</span> {payload.promptPlan.inferredLookback}</div>
                  <div className="text-muted-foreground md:col-span-2"><span className="font-semibold text-foreground">Matched intents:</span> {payload.promptPlan.matchedIntents.length ? payload.promptPlan.matchedIntents.join(', ') : 'template/default coverage'}</div>
                  <div className="text-muted-foreground md:col-span-2"><span className="font-semibold text-foreground">KQL/API-backed sections executed:</span> {payload.promptPlan.executedSections.length ? payload.promptPlan.executedSections.join(', ') : 'none'}</div>
                  {payload.promptPlan.skippedSections.length > 0 && <div className="text-amber-700 dark:text-amber-200 md:col-span-2"><span className="font-semibold">Skipped/partial sections:</span> {payload.promptPlan.skippedSections.join(', ')}</div>}
                </div>
              </div>
            )}
            {payload.ai.warning && <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{payload.ai.warning}</div>}
          </div>

          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Recommendations</h3>
              <div className="mt-4 space-y-3">
                {payload.recommendations.map((rec, index) => <div key={`${rec.title}-${index}`} className="rounded-xl border border-border bg-background p-4"><div className="font-bold text-foreground">{rec.priority} · {rec.title}</div><div className="mt-1 text-sm text-muted-foreground">{rec.details}</div></div>)}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Report Sections</h3>
              <div className="mt-4 grid gap-2">
                {allSections.map((section) => <button key={section.id} type="button" onClick={() => setSelectedSectionId(section.id)} className={`rounded-xl border p-3 text-left ${selectedSectionId === section.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}><div className="font-bold text-foreground">{section.title}</div><div className="text-xs text-muted-foreground">{section.rows.length} row(s) · {section.summary}</div></button>)}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><h3 className="text-lg font-black text-foreground">Evidence / Appendix Table</h3><p className="text-sm text-muted-foreground">Use the column ... menus for filter checklist, text filters and autosize.</p></div>
              <select value={selectedSectionId} onChange={(event) => setSelectedSectionId(event.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                {allSections.map((section) => <option key={section.id} value={section.id}>{section.title} ({section.rows.length})</option>)}
              </select>
            </div>
            {selectedSection?.warnings?.length ? <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{selectedSection.warnings.join(' ')}</div> : null}
            <div className="mt-4">
              <FilterableColumnTable<Record<string, unknown>>
                columns={columns}
                rows={selectedSection?.rows ?? []}
                storageKey={`report-builder-${customerId}-${selectedSection?.id ?? 'none'}`}
                rowKey={(row, index) => `${selectedSection?.id ?? 'row'}-${index}-${text(row[selectedSection?.columns[0] ?? ''])}`}
                emptyMessage="No rows for this report section or current filters."
                maxHeightClassName="max-h-[560px]"
              />
            </div>
          </div>

          {payload.diagnostics.length > 0 && <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"><strong>Partial data warnings:</strong><ul className="mt-2 list-disc pl-5">{payload.diagnostics.slice(0, 8).map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
        </>
      )}
    </div>
  );
}
